import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { loadGoogleMapsScript } from '../utils/googleMapsLoader';

const EventMap = ({ location, latitude, longitude, eventName, disabled = false }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Don't initialize if disabled
    if (disabled) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    // Load Google Maps script safely
    loadGoogleMapsScript()
      .then(() => {
        if (isMounted) {
          initializeMap();
        }
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      // Clean up map and marker instances with more robust error handling
      cleanupMap();
    };
  }, [disabled]);

  // Cleanup when disabled state changes
  useEffect(() => {
    if (disabled && map) {
      cleanupMap();
    }
  }, [disabled, map]);

  // Separate cleanup function for better error handling
  const cleanupMap = () => {
    try {
      if (marker) {
        try {
          marker.setMap(null);
        } catch (error) {
          console.warn('Error cleaning up marker:', error);
        }
        setMarker(null);
      }
      if (map) {
        try {
          // Clear any event listeners
          if (window.google && window.google.maps && window.google.maps.event) {
            window.google.maps.event.clearInstanceListeners(map);
          }
        } catch (error) {
          console.warn('Error clearing map listeners:', error);
        }
        setMap(null);
      }
    } catch (error) {
      console.warn('Error during map cleanup:', error);
    }
  };

  useEffect(() => {
    if (map && (latitude || longitude)) {
      // Add a small delay to ensure DOM is stable
      const timeoutId = setTimeout(() => {
        updateMapLocation();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [map, latitude, longitude]); // Remove location from dependencies to prevent excessive re-renders

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      setIsLoading(false);
      return;
    }

    try {
      // Check if the DOM element still exists
      if (!document.contains(mapRef.current)) {
        console.warn('Map container no longer exists in DOM');
        setIsLoading(false);
        return;
      }

      // Add a small delay to ensure the component is stable
      setTimeout(() => {
        // Double-check if component is still mounted and DOM element exists
        if (!mapRef.current || !document.contains(mapRef.current)) {
          console.warn('Map container no longer exists after delay');
          setIsLoading(false);
          return;
        }

        const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // Center of USA
        const mapOptions = {
          zoom: 13,
          center: defaultCenter,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        };

        try {
          const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
          setMap(newMap);
          setIsLoading(false);
        } catch (mapError) {
          console.error('Error creating Google Maps instance:', mapError);
          setIsLoading(false);
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
      setIsLoading(false);
    }
  };

  const updateMapLocation = () => {
    if (!map || !window.google || !window.google.maps) return;

    try {
      // Check if map is still valid and DOM element exists
      if (!map.getDiv || !map.getDiv() || !document.contains(map.getDiv())) {
        console.warn('Map is no longer valid or DOM element removed');
        return;
      }

      // Check if the map container still exists
      if (!mapRef.current || !document.contains(mapRef.current)) {
        console.warn('Map container no longer exists in DOM');
        return;
      }

      let center;
      if (latitude && longitude) {
        center = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
      } else if (location) {
        // If no coordinates but location text, try to geocode
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: location }, (results, status) => {
          // Double-check DOM validity before proceeding
          if (status === 'OK' && results[0] && map && map.getDiv && document.contains(map.getDiv()) && mapRef.current && document.contains(mapRef.current)) {
            const location = results[0].geometry.location;
            center = { lat: location.lat(), lng: location.lng() };
            try {
              map.setCenter(center);
              addMarker(center);
            } catch (error) {
              console.warn('Error updating map after geocoding:', error);
            }
          }
        });
        return;
      } else {
        return;
      }

      map.setCenter(center);
      addMarker(center);
    } catch (error) {
      console.error('Error updating map location:', error);
    }
  };

  const addMarker = (position) => {
    if (!map || !window.google || !window.google.maps) return;

    try {
      // Check if map is still valid and DOM element exists
      if (!map.getDiv || !map.getDiv() || !document.contains(map.getDiv())) {
        console.warn('Map is no longer valid for adding marker');
        return;
      }

      // Check if the map container still exists
      if (!mapRef.current || !document.contains(mapRef.current)) {
        console.warn('Map container no longer exists for adding marker');
        return;
      }

      // Remove existing marker
      if (marker) {
        try {
          marker.setMap(null);
        } catch (error) {
          console.warn('Error removing existing marker:', error);
        }
      }

      // Add new marker
      const newMarker = new window.google.maps.Marker({
        position,
        map,
        title: eventName || 'Event Location',
        animation: window.google.maps.Animation.DROP,
      });

      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h3 style="margin: 0 0 8px 0; font-weight: bold;">${eventName || 'Event'}</h3>
            <p style="margin: 0; color: #666;">${location || 'Location'}</p>
          </div>
        `,
      });

      newMarker.addListener('click', () => {
        // Double-check DOM validity before opening info window
        if (map && map.getDiv && document.contains(map.getDiv()) && mapRef.current && document.contains(mapRef.current)) {
          try {
            infoWindow.open(map, newMarker);
          } catch (error) {
            console.warn('Error opening info window:', error);
          }
        }
      });

      setMarker(newMarker);
    } catch (error) {
      console.error('Error adding marker:', error);
    }
  };

  const openInGoogleMaps = () => {
    let url;
    if (latitude && longitude) {
      url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    } else if (location) {
      url = `https://www.google.com/maps?q=${encodeURIComponent(location)}`;
    } else {
      return;
    }
    window.open(url, '_blank');
  };

  const getDirections = () => {
    let url;
    if (latitude && longitude) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    } else if (location) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`;
    } else {
      return;
    }
    window.open(url, '_blank');
  };

  // Don't render if we don't have stable location data
  if (!location || !location.trim()) {
    return null;
  }

  // If disabled, show a simple location display without the map
  if (disabled) {
    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <MapPin size={16} />
            Event Location
          </h4>
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-300">
          <p className="text-sm text-gray-700">
            <strong>Address:</strong> {location}
          </p>
          {latitude && longitude && (
            <p className="text-xs text-gray-500 mt-1">
              Coordinates: {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-2 italic">
            Map preview disabled while editing
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <MapPin size={16} />
          Event Location
        </h4>
        <div className="flex gap-2">
          <button
            onClick={getDirections}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            title="Get Directions"
          >
            <Navigation size={14} />
            Directions
          </button>
          <button
            onClick={openInGoogleMaps}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            title="Open in Google Maps"
          >
            <ExternalLink size={14} />
            Open Map
          </button>
        </div>
      </div>

      <div className="relative">
        <div
          ref={mapRef}
          className="w-full h-64 rounded-lg border border-gray-300"
          style={{ minHeight: '256px' }}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </div>
        
        {location && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Address:</strong> {location}
            </p>
            {latitude && longitude && (
              <p className="text-xs text-gray-500 mt-1">
                Coordinates: {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventMap; 